import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
//dls
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
//types
import { FilterCakeItems, CartMessagePersonalisedProps } from "@swiggy-private/cart-types";
//styles
import {
    FoodSymbolIcon,
    ItemDescWrapper,
    ItemsContainer,
    SubItemContainer,
    Separator,
    SelectCakeContainer,
    SeparatorDesc,
    StyledImage,
    ItemContent,
    CakeName,
} from "./styles";
//icons
import { default as ArrowRight } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/16";
//components
import CartMessageOnCake from "./components/CartMessageOnCake";
//constants
import { CAKE_SELECT_TEXT, STATIC_ASSETS_PATH, IMAGE_DIMENSIONS, SEPARATOR } from "./constant";
import { useEffectOnce } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { CAKE_TEST_IDS } from "@swiggy-private/cart-test-ids";

const CartMessagePersonalised = ({
    filterCakeItems,
    cakeMessageHalfCardState,
    setCakeMessageHalfCardState,
    predefinedWordsForCakeMessage,
    maxCharactersPerCakeMessage,
    submitCakeMessage,
    onHalfCardImpression,
    onHalfCardClick,
    onMessageHalfCardImpression,
    onMessageHalfCardClick,
}: CartMessagePersonalisedProps) => {
    const [selectedCakeItem, setSelectedCakeItem] = useState<FilterCakeItems>();

    const isOpenSelectCakeHalfCard = cakeMessageHalfCardState.showSelectCakeHalfCard;
    const isOpenAddCakeMessageHalfCard = cakeMessageHalfCardState.showAddCakeMessageHalfCard;

    useEffectOnce(() => {
        if (isOpenSelectCakeHalfCard) {
            onHalfCardImpression();
        } else {
            onMessageHalfCardImpression();
        }
    });

    useEffect(() => {
        if (filterCakeItems.length === 1) {
            setSelectedCakeItem(filterCakeItems[0]);
        }
    }, [filterCakeItems]);

    const handleClick = useCallback(
        (item: FilterCakeItems) => {
            onHalfCardClick();
            setSelectedCakeItem(item);
            setCakeMessageHalfCardState({
                showSelectCakeHalfCard: false,
                showAddCakeMessageHalfCard: true,
            });
        },
        [onHalfCardClick, setCakeMessageHalfCardState],
    );

    const handleClose = useCallback(() => {
        setCakeMessageHalfCardState({
            showSelectCakeHalfCard: false,
            showAddCakeMessageHalfCard: false,
        });
    }, [setCakeMessageHalfCardState]);

    // select cake UI
    const selectCakeRender = useMemo(() => {
        if (isEmpty(filterCakeItems)) {
            return null;
        }
        return (
            <SelectCakeContainer data-testid={CAKE_TEST_IDS.SELECT_HALF_CARD}>
                <Text variant="V2.subheading1.neutral.bold" color="text_High_Emphasis">
                    {CAKE_SELECT_TEXT.SELECT_CAKE}
                </Text>
                <ItemsContainer>
                    {filterCakeItems.map((item, index) => {
                        const showSeparator =
                            !isEmpty(item?.customer_request_messages?.[item.index]) &&
                            !isEmpty(item?.variants?.[0]?.name);
                        return (
                            <React.Fragment
                                key={`${item.item_id || ""}${item?.unId || ""}${item.currentIndex?.toString() || ""}`}
                            >
                                <SubItemContainer onClick={() => handleClick(item)}>
                                    <StyledImage
                                        cloudinaryId={item?.cloudinaryImageId || STATIC_ASSETS_PATH.DEFAULT_PLACEHOLDER}
                                        alt={item.name}
                                        fit="cover"
                                        height={IMAGE_DIMENSIONS.HEIGHT}
                                        width={IMAGE_DIMENSIONS.WIDTH}
                                    />
                                    <ItemContent>
                                        <ItemDescWrapper>
                                            <FoodSymbolIcon vegType={item.isVeg} />
                                            <CakeName color="text_High_Emphasis">{item.name}</CakeName>
                                            {!isEmpty(item?.currentIndex) && (
                                                <Text
                                                    variant="V2.body1.neutral.bold"
                                                    color="text_High_Emphasis"
                                                    width="fit-content"
                                                >
                                                    {`(${item?.currentIndex})`}
                                                </Text>
                                            )}
                                        </ItemDescWrapper>
                                        <ItemDescWrapper>
                                            {!isEmpty(item.variants?.[0]?.name) && (
                                                <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                                                    {item.variants?.[0]?.name}
                                                </Text>
                                            )}
                                            {showSeparator && <SeparatorDesc>{SEPARATOR}</SeparatorDesc>}
                                            <Text variant="V2.body3.neutral.regular" color="text_Low_Emphasis">
                                                {item?.customer_request_messages?.[item.index] || ""}
                                            </Text>
                                        </ItemDescWrapper>
                                    </ItemContent>
                                    <GenericIcon Icon={ArrowRight} fillColor="text_Low_Emphasis" />
                                </SubItemContainer>
                                {index !== filterCakeItems.length - 1 && <Separator />}
                            </React.Fragment>
                        );
                    })}
                </ItemsContainer>
            </SelectCakeContainer>
        );
    }, [filterCakeItems, handleClick]);

    //add cake message UI
    const addCakeMessageRender = useMemo(() => {
        if (isEmpty(selectedCakeItem)) {
            return null;
        }
        return (
            <CartMessageOnCake
                maxCharactersPerCakeMessage={maxCharactersPerCakeMessage}
                predefinedWordsForCakeMessage={predefinedWordsForCakeMessage}
                handleConfirmMessage={submitCakeMessage}
                selectedCakeItem={selectedCakeItem}
                onHalfCardClose={handleClose}
                onMessageHalfCardClick={onMessageHalfCardClick}
            />
        );
    }, [
        handleClose,
        maxCharactersPerCakeMessage,
        onMessageHalfCardClick,
        predefinedWordsForCakeMessage,
        selectedCakeItem,
        submitCakeMessage,
    ]);

    return (
        <HalfCard
            isOpen={isOpenSelectCakeHalfCard || isOpenAddCakeMessageHalfCard}
            onClose={handleClose}
            hasCustomPadding={isOpenSelectCakeHalfCard}
            closeOnOverlayClick={true}
            variant="v3"
        >
            {isOpenSelectCakeHalfCard && selectCakeRender}
            {isOpenAddCakeMessageHalfCard && addCakeMessageRender}
        </HalfCard>
    );
};

export default memo(CartMessagePersonalised);
